import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DeleteAccountDialog = ({ open, onOpenChange, onDeleted }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const close = (v) => {
    if (loading) return;
    if (!v) { setPassword(''); setError(''); }
    onOpenChange(v);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!password) { setError('Please enter your password.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/users/me/delete`, { password });
      toast.success(data.message || 'Your account has been permanently deleted.', { duration: 6000 });
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not delete account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={close}>
      <AlertDialogContent className="border-[#E4E4E7]" data-testid="delete-account-dialog">
        <form onSubmit={handleDelete} className="space-y-4">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[#09090B]">Delete your account permanently?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#71717A]">
              This deletes your profile, stats, chat messages and voice notes, friends, squad invites and rooms you created.
              It cannot be undone. Consider exporting your data first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete-account-password">Confirm Password</Label>
            <Input id="delete-account-password" type="password" value={password} autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)} placeholder="Your current password"
              className="border-[#E4E4E7]" data-testid="delete-account-password-input" />
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="delete-account-error">{error}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading} data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-account-confirm">
              {loading ? 'Deleting...' : 'Delete Forever'}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
